import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, Briefcase, User } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

const DEMO_ACCOUNTS = [
  { label: "Login as Admin", email: "admin@mlilycrm.demo", password: "Admin123!", icon: Shield, desc: "Full company access — Super Admin" },
  { label: "Login as General Manager", email: "manager@mlilycrm.demo", password: "Manager123!", icon: Briefcase, desc: "All agents, sales & reports" },
  { label: "Login as Jarred Davis", email: "jarred@mlilycrm.demo", password: "Jarred123!", icon: User, desc: "Sales Agent — NSW / ACT only" },
];

const resolve = (u) => (u?.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem("mlily_remember_email") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("mlily_remember_email"));
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({ logo_url: "", feature_photo_url: "" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/branding").then(r => setBrand(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!brand.favicon_url) return;
    const link = document.querySelector("link[rel='icon']") || document.createElement("link");
    link.rel = "icon";
    link.href = resolve(brand.favicon_url);
    document.head.appendChild(link);
  }, [brand.favicon_url]);

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (e, override) => {
    if (e) e.preventDefault();
    const em = override?.email ?? email;
    const pw = override?.password ?? password;
    if (override) { setEmail(em); setPassword(pw); }
    setBusy(true);
    try {
      await login(em, pw);
      if (remember || override) localStorage.setItem("mlily_remember_email", em);
      else localStorage.removeItem("mlily_remember_email");
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  const logoSrc = resolve(brand.logo_url);
  const photoSrc = resolve(brand.feature_photo_url);

  return (
    <div className="min-h-screen bg-white flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* Mobile banner (short) */}
      <div className="relative lg:hidden h-40 overflow-hidden">
        {photoSrc && <img src={photoSrc} alt="MLILY" className="w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,31,84,0.55), rgba(0,61,165,0.45))" }} />
        {logoSrc && (
          <div className="absolute inset-0 flex items-center justify-center">
            <img src={logoSrc} alt="MLILY" className="w-20 h-20 rounded-lg shadow-lg" />
          </div>
        )}
      </div>

      {/* LEFT — Login panel */}
      <div className="flex items-center justify-center px-6 py-8 sm:py-12 lg:px-14">
        <div className="w-full max-w-md space-y-6" data-testid="login-panel">
          <div className="hidden lg:flex items-center gap-3">
            {logoSrc && <img src={logoSrc} alt="MLILY" className="w-14 h-14 rounded-lg" data-testid="login-logo" />}
            <div>
              <div className="text-[10px] tracking-[0.3em] text-[#003DA5] font-semibold">MLILY</div>
              <div className="text-lg font-heading font-bold text-[#001F54]">CRM</div>
            </div>
          </div>

          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-[#001F54]">Welcome to MLILY CRM</h1>
            <p className="mt-2 text-slate-600">Store Visits, Customer Activity & Sales Performance</p>
          </div>

          <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-slate-300 rounded-lg outline-none transition"
                placeholder="you@mlily.com.au"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-slate-300 rounded-lg outline-none transition"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-[#003DA5]" />
                <span className="text-slate-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={async () => {
                  const em = prompt("Enter your MLILY email to receive a password reset link:");
                  if (!em) return;
                  try {
                    await api.post("/auth/forgot-password", { email: em });
                    toast.success("If that email exists, a reset link has been sent.");
                  } catch { toast.error("Could not send reset link"); }
                }}
                className="text-[#003DA5] font-semibold hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="mlily-btn w-full h-12 rounded-lg tracking-wide"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Demo Login</span>
            </div>
          </div>

          <div className="space-y-2">
            {DEMO_ACCOUNTS.map((d) => (
              <button
                key={d.email}
                type="button"
                disabled={busy}
                onClick={() => doLogin(null, { email: d.email, password: d.password })}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-[#003DA5] hover:bg-[#F3F7FC] transition text-left disabled:opacity-60"
                data-testid={`demo-login-${d.email.split("@")[0]}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#001F54] text-white grid place-items-center shrink-0">
                  <d.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#001F54]">{d.label}</div>
                  <div className="text-xs text-slate-500 truncate">{d.desc}</div>
                </div>
                <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#FFF3DD] text-[#8A5A00] border border-[#FFD98A]">DEMO</span>
              </button>
            ))}
          </div>

          <div className="text-[11px] text-slate-500 border-t pt-3">
            All demo accounts above are pre-loaded with sample data. <b>Replace the demo passwords before deploying with real company information.</b>
          </div>

          <div className="text-center pt-2 text-xs text-slate-500 font-medium">
            MLILY Australia — Internal Business System
          </div>
        </div>
      </div>

      {/* RIGHT — Feature photo (desktop only) */}
      <div className="hidden lg:block relative overflow-hidden">
        {photoSrc && <img src={photoSrc} alt="MLILY sleep experience" className="absolute inset-0 w-full h-full object-cover" data-testid="login-feature-photo" />}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(0,31,84,0.68) 0%, rgba(0,61,165,0.55) 60%, rgba(0,31,84,0.72) 100%)" }}
        />
        <div className="relative h-full flex items-end p-12">
          <div className="max-w-md text-white">
            <h2 className="text-4xl xl:text-5xl font-heading font-bold leading-tight" data-testid="feature-heading">
              Empowering our sales team
            </h2>
            <div className="mt-4 h-1 w-16 rounded-full bg-[#FFB547]" />
            <p className="mt-5 text-white/85 text-lg leading-relaxed">
              Manage store visits, training, customer activity and sales performance in one secure platform.
            </p>
            <div className="mt-8 flex items-center gap-2 text-xs text-white/70 tracking-[0.25em] uppercase font-semibold">
              <span className="w-8 h-px bg-white/40" /> MLILY Sleep Innovation
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
